import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  Clock,
  Loader2,
  Search,
  XCircle,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import { useAllExams, useMyExams } from "@/hooks/use-exams";
import {
  useCancelExamAccess,
  useMyExamRequests,
  useRequestExamAccess,
} from "@/hooks/use-admin";
import { createErrorMessage } from "@/lib/error-message";
import type { Exam, ExamRequestStatus } from "@/lib/types";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ExamInquiryCard } from "@/components/exam-inquiry-card";

type Row = {
  exam: Exam;
  status: ExamRequestStatus | "assigned" | "available";
  reason?: string;
};

const normalize = (s: string | undefined) => (s ?? "").toLowerCase();

export const Route = createFileRoute("/_main/request-exam")({
  component: RequestExamPage,
});

function RequestExamPage() {
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();
  const { t } = useLanguage();

  const allExamsQuery = useAllExams();
  const myExamsQuery = useMyExams();
  const myRequestsQuery = useMyExamRequests();
  const requestMutation = useRequestExamAccess();
  const cancelMutation = useCancelExamAccess();

  const loading =
    allExamsQuery.isLoading ||
    myExamsQuery.isLoading ||
    myRequestsQuery.isLoading;
  const error =
    allExamsQuery.error ??
    myExamsQuery.error ??
    myRequestsQuery.error ??
    requestMutation.error ??
    cancelMutation.error ??
    null;

  const all = useMemo(() => allExamsQuery.data ?? [], [allExamsQuery.data]);
  const assigned = useMemo(() => myExamsQuery.data ?? [], [myExamsQuery.data]);
  const requests = useMemo(
    () => myRequestsQuery.data ?? [],
    [myRequestsQuery.data],
  );

  const [query, setQuery] = useState("");

  const rows = useMemo<Row[]>(() => {
    const assignedSet = new Set(assigned.map((e) => e.id));
    const reqMap = new Map<string, (typeof requests)[number]>();
    for (const r of requests) reqMap.set(r.exam_slug, r);

    return all.map((exam) => {
      if (assignedSet.has(exam.id)) {
        return { exam, status: "assigned" };
      }
      const req = reqMap.get(exam.id);
      if (req) {
        // Approved request without an active assignment means access was
        // revoked — let the user re-apply instead of leaving the card stuck
        // in a "assigned" state with no action.
        if (req.status === "approved") {
          return { exam, status: "available" };
        }
        return { exam, status: req.status, reason: req.reason };
      }
      return { exam, status: "available" };
    });
  }, [all, assigned, requests]);

  const submitting =
    requestMutation.isPending && requestMutation.variables
      ? requestMutation.variables.examSlug
      : null;
  const cancelling =
    cancelMutation.isPending && cancelMutation.variables
      ? cancelMutation.variables.examSlug
      : null;

  const filtered = useMemo(() => {
    const q = normalize(query).trim();
    if (!q) return rows;
    return rows.filter(({ exam }) => {
      if (normalize(exam.name).includes(q)) return true;
      if (normalize(exam.description).includes(q)) return true;
      return false;
    });
  }, [rows, query]);

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-4xl">
      <button
        onClick={() => navigate({ to: "/dashboard" })}
        className="mb-6 inline-flex cursor-pointer items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        {t("request_exam.back")}
      </button>

      <h1 className="text-2xl font-bold sm:text-3xl">
        {t("request_exam.title")}
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {t("request_exam.desc")}
      </p>

      {error && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-900 dark:bg-red-950 dark:text-red-400">
          {createErrorMessage(error)}
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="mt-6 relative">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("exam_picker.search_placeholder")}
            className="h-10 pl-9"
          />
        </div>
      )}

      {loading ? (
        <div className="mt-8 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
        </div>
      ) : rows.length === 0 ? (
        <div className="mt-8 rounded-lg border border-dashed py-12 text-center text-sm text-muted-foreground">
          {t("request_exam.no_exams")}
        </div>
      ) : filtered.length === 0 ? (
        <div className="mt-8 rounded-lg border border-dashed py-12 text-center text-sm text-muted-foreground">
          {t("exam_picker.no_results")}
        </div>
      ) : (
        <>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {filtered.map(({ exam, status }) => {
            return (
              <Card key={exam.id}>
                <CardHeader>
                  <div className="mb-2 flex items-center justify-between">
                    <div className="rounded-lg bg-blue-50 p-2 dark:bg-blue-950">
                      <BookOpen className="size-5 text-blue-500" />
                    </div>
                    <StatusBadge status={status} t={t} />
                  </div>
                  <CardTitle className="text-lg">{exam.name}</CardTitle>
                  <CardDescription>{exam.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  {status === "assigned" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full cursor-pointer"
                      onClick={() =>
                        navigate({
                          to: "/exams/$slug",
                          params: { slug: exam.id },
                        })
                      }
                    >
                      {t("request_exam.go_to_exam")}
                    </Button>
                  )}
                  {status === "available" && (
                    <Button
                      size="sm"
                      className="w-full cursor-pointer"
                      disabled={submitting === exam.id}
                      onClick={() =>
                        requestMutation.mutate({ examSlug: exam.id })
                      }
                    >
                      {submitting === exam.id ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        t("request_exam.request")
                      )}
                    </Button>
                  )}
                  {status === "denied" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full cursor-pointer"
                      disabled={submitting === exam.id}
                      onClick={() =>
                        requestMutation.mutate({ examSlug: exam.id })
                      }
                    >
                      {submitting === exam.id ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        t("request_exam.request_again")
                      )}
                    </Button>
                  )}
                  {status === "pending" && (
                    <div className="space-y-2">
                      <p className="text-center text-xs text-muted-foreground">
                        {t("request_exam.pending_desc")}
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full cursor-pointer"
                        disabled={cancelling === exam.id}
                        onClick={() => {
                          if (window.confirm(t("request_exam.cancel_confirm"))) {
                            cancelMutation.mutate({ examSlug: exam.id });
                          }
                        }}
                      >
                        {cancelling === exam.id ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : (
                          t("request_exam.cancel_request")
                        )}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
        <div className="mt-8">
          <ExamInquiryCard />
        </div>
        </>
      )}
    </div>
  );
}

function StatusBadge({
  status,
  t,
}: {
  status: Row["status"];
  t: (key: string) => string;
}) {
  switch (status) {
    case "assigned":
    case "approved":
      return (
        <Badge className="gap-1 bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400">
          <CheckCircle2 className="size-3" />
          {t("request_exam.status_assigned")}
        </Badge>
      );
    case "pending":
      return (
        <Badge variant="secondary" className="gap-1">
          <Clock className="size-3" />
          {t("request_exam.status_pending")}
        </Badge>
      );
    case "denied":
      return (
        <Badge className="gap-1 bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400">
          <XCircle className="size-3" />
          {t("request_exam.status_denied")}
        </Badge>
      );
    default:
      return null;
  }
}
